import React from 'react';
import { FaBoxOpen, FaCartShopping, FaDumbbell, FaGasPump } from 'react-icons/fa6';
import './PlatformHub.css';

// The hub is drawn on a 480 x 480 board. Each product is one object here:
// pick an angle (degrees, 0 = right, positive = clockwise) and the position,
// the connector line and the label are worked out below. Add a product by adding one line.
const CENTER = 240;
const LIVE_RADIUS = 168;
const SOON_RADIUS = 150;

const PRODUCTS = [
  { key: 'orders',   Icon: FaBoxOpen,      label: 'Effy Orders', status: 'Live', angle: -33 },
  { key: 'fuel',     Icon: FaGasPump,      label: 'Effy Fuel',   status: 'Live', angle: -149 },
  { key: 'commerce', Icon: FaCartShopping, label: 'E-Commerce',  status: 'Soon', angle: 43 },
  { key: 'gym',      Icon: FaDumbbell,     label: 'Gym App',     status: 'Soon', angle: 152 },
];

// Soft curved line from the centre to a node (one gentle bend, so it never looks like a ruler line).
function buildPath(cx, cy) {
  const dx = cx - CENTER;
  const dy = cy - CENTER;
  const length = Math.sqrt(dx * dx + dy * dy);
  const bendX = CENTER + dx / 2 + (-dy / length) * 16;
  const bendY = CENTER + dy / 2 + (dx / length) * 16;
  return `M${CENTER},${CENTER} Q${bendX.toFixed(1)},${bendY.toFixed(1)} ${cx.toFixed(1)},${cy.toFixed(1)}`;
}

function placeNode(product) {
  let radius = SOON_RADIUS;
  let size = 25;
  if (product.status === 'Live') {
    radius = LIVE_RADIUS;
    size = 36;
  }
  const radians = (product.angle * Math.PI) / 180;
  const cx = CENTER + radius * Math.cos(radians);
  const cy = CENTER + radius * Math.sin(radians);
  return { ...product, cx, cy, r: size, path: buildPath(cx, cy) };
}

const NODES = PRODUCTS.map(placeNode);
const LIVE_NODES = NODES.filter(n => n.status === 'Live');
const SOON_NODES = NODES.filter(n => n.status === 'Soon');

export default function PlatformHub() {
  return (
    <svg viewBox="0 0 480 480" fill="none" xmlns="http://www.w3.org/2000/svg" className="hub-svg" aria-hidden="true">
      <defs>
        <filter id="hub-shadow" x="-30%" y="-30%" width="160%" height="170%">
          <feDropShadow dx="0" dy="2" stdDeviation="4" floodColor="#1a0d24" floodOpacity="0.12" />
        </filter>
      </defs>

      {/* Orbit ring */}
      <circle cx={CENTER} cy={CENTER} r={LIVE_RADIUS} fill="none" stroke="rgba(26,13,36,0.14)" strokeWidth="1" strokeDasharray="2 7" />

      {/* Connectors */}
      {LIVE_NODES.map(n => (
        <path key={n.key} d={n.path} stroke="#5f217c" strokeOpacity="0.55" strokeWidth="2" fill="none" className="hub-line" />
      ))}
      {SOON_NODES.map(n => (
        <path key={n.key} d={n.path} stroke="rgba(26,13,36,0.22)" strokeWidth="1.5" strokeDasharray="3 5" fill="none" className="hub-line hub-line--soon" />
      ))}

      {/* One calm pulse ring around the core */}
      <circle cx={CENTER} cy={CENTER} r="64" fill="none" stroke="#5f217c" strokeWidth="1.5" className="hub-pulse" />

      {/* Core: flat plum circle, thin gold ring, growth mark */}
      <circle cx={CENTER} cy={CENTER} r="58" fill="#5f217c" filter="url(#hub-shadow)" />
      <circle cx={CENTER} cy={CENTER} r="64" fill="none" stroke="#c9a000" strokeWidth="1.5" opacity="0.7" />
      <rect x="226" y="212" width="6" height="10" rx="2" fill="#fff" />
      <rect x="237" y="206" width="6" height="16" rx="2" fill="#fff" />
      <rect x="248" y="200" width="6" height="22" rx="2" fill="#c9a000" />
      <text x={CENTER} y="244" fontFamily="'Figtree Variable', 'Figtree', sans-serif" fontSize="22" fontWeight="700" fill="#fff" textAnchor="middle">Effy</text>
      <text x={CENTER} y="262" fontFamily="'Figtree Variable', 'Figtree', sans-serif" fontSize="11" fontWeight="600" letterSpacing="0.66" fill="rgba(255,255,255,0.8)" textAnchor="middle">PLATFORM</text>

      {/* Live nodes */}
      {LIVE_NODES.map(({ key, cx, cy, r, Icon, label }) => (
        <g key={key} className="hub-node hub-node--live">
          <circle cx={cx} cy={cy} r={r} fill="#fff" stroke="#5f217c" strokeWidth="2" filter="url(#hub-shadow)" />
          <foreignObject x={cx - 17} y={cy - 17} width="34" height="34">
            <div className="hub-node-icon hub-node-icon--live">
              <Icon />
            </div>
          </foreignObject>
          <rect x={cx - 52} y={cy + 44} width="104" height="24" rx="12" fill="#fff" stroke="rgba(26,13,36,0.12)" />
          <text x={cx} y={cy + 61} className="hub-label" textAnchor="middle">{label}</text>
          <rect x={cx + 20} y={cy - 50} width="44" height="18" rx="9" fill="rgba(95,33,124,0.1)" />
          <circle cx={cx + 31} cy={cy - 41} r="2.6" fill="#5f217c" className="hub-live-dot" />
          <text x={cx + 38} y={cy - 37.5} className="hub-chip">Live</text>
        </g>
      ))}

      {/* Upcoming nodes */}
      {SOON_NODES.map(({ key, cx, cy, r, Icon, label }) => (
        <g key={key} className="hub-node hub-node--soon">
          <circle cx={cx} cy={cy} r={r} fill="#fff" stroke="rgba(26,13,36,0.25)" strokeWidth="1.5" strokeDasharray="4 3" />
          <foreignObject x={cx - 13} y={cy - 13} width="26" height="26">
            <div className="hub-node-icon hub-node-icon--soon">
              <Icon />
            </div>
          </foreignObject>
          <text x={cx} y={cy + 44} className="hub-label hub-label--soon" textAnchor="middle">{label}</text>
        </g>
      ))}
    </svg>
  );
}
